<template>
  <div class="contact">
    <div class="root">
      <h1>{{ $t("contact.title") }}</h1>

      <!-- 期間限定お知らせ -->
      <div v-if="activeNotice" class="notice-box">
        <p class="notice-title">{{ $t(activeNotice.titleKey) }}</p>
        <p class="notice-message">{{ $t(activeNotice.messageKey) }}</p>
      </div>

      <p class="contact-message">
        {{ $t("contact.thankYou") }}<br /><br />
        <span class="contact-message-title">{{ $t("contact.beforeContact.title") }}</span
        ><br />
        {{ $t("contact.beforeContact.helpPageDescription") }}<br />
        {{ $t("contact.beforeContact.checkHelpPage")
        }}<a class="bold" :href="help" target="_blank" rel="noopener noreferrer">{{
          $t("contact.beforeContact.helpPageLink")
        }}</a
        >{{ $t("contact.beforeContact.checkHelpPageSuffix") }}<br /><br />
        {{ $t("contact.beforeContact.stillNotResolved") }}<br /><br />
        <b>{{ $t("contact.inquiryGuidelines.questionOrBugTitle") }}</b
        ><br />
        {{ $t("contact.inquiryGuidelines.requireInvestigationInfo") }}<br />
        {{ $t("contact.inquiryGuidelines.delayWarning") }}<br /><br />
        <b>{{ $t("contact.inquiryGuidelines.requestTitle") }}</b
        ><br />
        {{ $t("contact.inquiryGuidelines.requestReference") }}<br />
        {{ $t("contact.inquiryGuidelines.requestForm.prefix")
        }}<a class="bold" :href="requestFormUrl" target="_blank" rel="noopener noreferrer">{{
          $t("contact.inquiryGuidelines.requestForm.link")
        }}</a
        >{{ $t("contact.inquiryGuidelines.requestForm.suffix") }}<br />
        {{ $t("contact.inquiryGuidelines.noReplyToRequest") }}<br />
        {{ $t("contact.inquiryGuidelines.checkHelp.prefix")
        }}<a class="bold" :href="help" target="_blank" rel="noopener noreferrer">{{
          $t("contact.inquiryGuidelines.checkHelp.link")
        }}</a
        >{{ $t("contact.inquiryGuidelines.checkHelp.suffix") }}
      </p>
      <a class="button email" :href="mail">
        <img src="@/assets/img/static/sns-icon-mail.png" />
        <div class="email-txt">{{ $t("contact.sendByEmail") }}</div>
      </a>

      <!-- メーラー起動できない場合の代替手段 -->
      <div class="fallback-link">
        <button @click="showFallbackOptions" class="link-button">
          {{ $t("contact.mailerFallback.linkText") }}
        </button>
      </div>

      <!-- フォールバック情報表示 -->
      <div v-if="showFallback" class="fallback-section">
        <h3>{{ $t("contact.mailerFallback.title") }}</h3>
        <p>{{ $t("contact.mailerFallback.description") }}</p>

        <div class="fallback-info">
          <div class="info-item">
            <strong>{{ $t("contact.mailerFallback.to") }}</strong>
            <div class="input-row">
              <input type="text" readonly :value="mailTo" class="copy-input" ref="mailToInput" />
              <button @click="() => copyToClipboard('mailTo')" class="copy-btn">
                {{ $t("contact.mailerFallback.copy") }}
              </button>
            </div>
          </div>

          <div class="info-item">
            <strong>{{ $t("contact.mailerFallback.subject") }}</strong>
            <div class="input-row">
              <input type="text" readonly :value="mailSubject" class="copy-input" ref="subjectInput" />
              <button @click="() => copyToClipboard('subject')" class="copy-btn">
                {{ $t("contact.mailerFallback.copy") }}
              </button>
            </div>
          </div>

          <div class="info-item">
            <strong>{{ $t("contact.mailerFallback.body") }}</strong>
            <div class="input-row">
              <textarea readonly :value="mailBody" class="copy-textarea" ref="bodyTextarea"></textarea>
              <button @click="() => copyToClipboard('body')" class="copy-btn">
                {{ $t("contact.mailerFallback.copy") }}
              </button>
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <button @click="copyAllMailInfo" class="copy-all-btn">
            {{ $t("contact.mailerFallback.copyAll") }}
          </button>
          <button @click="hideFallback" class="close-btn">
            {{ $t("contact.mailerFallback.close") }}
          </button>
        </div>
      </div>
      <p>
        {{ $t("contact.faqSection.prefix") }}
        <a class="bold" :href="help" target="_blank" rel="noopener noreferrer">{{
          $t("contact.faqSection.linkText")
        }}</a>
        {{ $t("contact.faqSection.suffix1") }}<br />
        {{ $t("contact.faqSection.suffix2") }}
      </p>
      <!-- クリップボードにコピー用 -->
      <h2>{{ $t("contact.investigationInfo.title") }}</h2>
      <textarea type="text" readonly ref="textarea" :value="diagInfo"></textarea>
      <button class="button-copy" @click="() => copyToClipboard()">
        {{ $t("contact.investigationInfo.copyButton") }}
      </button>
      <div class="info" v-if="message">{{ message }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Auth } from "aws-amplify";
import { prepareContactData, ContactData } from "@/lib/utils/contact";
import { ContactNotice, getActiveContactNotice } from "@/lib/config/contactNotices";

export default Vue.extend({
  // NOTE: metaタグの設定
  metaInfo() {
    if (process.env.NODE_ENV === "production") {
      return {};
    }
    return {
      meta: [
        {
          name: "robots",
          content: "none",
        },
      ],
    };
  },
  data() {
    return {
      userId: "",
      message: "",
      idPoolId: "",
      showFallback: false,
      mailTo: "nola.cs@indent.co.jp",
      mailSubject: "",
      mailBody: "",
      contactData: null as ContactData | null,
    };
  },
  computed: {
    activeNotice(): ContactNotice | null {
      return getActiveContactNotice();
    },
    diagInfo() {
      const BR = "\n";
      const text = `${this.$t("contact.diagInfo.typeLabel")}${BR}${this.$t(
        "contact.diagInfo.typeOptions"
      )}${BR}${this.$t("contact.diagInfo.typeNote")}${BR}${BR}${this.$t(
        "contact.diagInfo.occurredDateLabel"
      )}${BR}${this.$t("contact.diagInfo.occurredDateExample")}${BR}${this.$t(
        "contact.diagInfo.occurredDateNote"
      )}${BR}${BR}${this.$t("contact.diagInfo.detailsLabel")}${BR}${this.$t(
        "contact.diagInfo.detailsPlaceholder"
      )}${BR}${BR}${this.$t("contact.diagInfo.bugTips")}${BR}${this.$t("contact.diagInfo.bugExample")}${BR}${this.$t(
        "contact.diagInfo.manuscriptNote"
      )}${BR}${this.$t("contact.diagInfo.fishNote")}${BR}${this.$t(
        "contact.diagInfo.subscriptionNote"
      )}${BR}${BR}${this.$t("contact.diagInfo.requiredForBug")}${BR}${BR}${this.$t(
        "contact.diagInfo.browserInfoLabel"
      )}${BR}${window.navigator.userAgent}${BR}${this.$t("contact.diagInfo.deviceInfoLabel")}${BR}${
        window.navigator.platform
      }${BR}${this.$t("contact.diagInfo.userIdLabel")}${BR}${this.userId}${BR}${this.$t(
        "contact.diagInfo.investigationInfoLabel"
      )}${BR}${this.idPoolId}${BR}`;
      return text;
    },
    mail(): string {
      if (!this.contactData) {
        return `mailto:${this.mailTo}`;
      }

      return `mailto:${this.mailTo}?subject=${encodeURIComponent(this.contactData.subject)}&body=${encodeURIComponent(
        this.contactData.body
      )}`;
    },
    help() {
      return process.env.VUE_APP_NOTION_HELP!;
    },
    requestFormUrl() {
      return process.env.VUE_APP_REQUEST_FORM_URL!;
    },
  },
  async created() {
    let userId = null;
    try {
      const user = await Auth.currentUserPoolUser();
      userId = user.signInUserSession.idToken.payload.sub;
    } catch (error) {
      // do nothing
    }
    this.userId = userId ?? this.$t("contact.notLoggedIn");

    const currentCredentials = (await Auth.currentCredentials()) as any;
    if (currentCredentials && currentCredentials.data) {
      this.idPoolId = currentCredentials.data.IdentityId;
    }

    // お問い合わせデータを事前生成
    const baseSubject = this.$t("contact.email.subject");
    const body = this.getEmailBody();
    this.contactData = prepareContactData(baseSubject, body, this.userId);
  },
  methods: {
    /**
     * メール本文を生成する共通メソッド
     */
    getEmailBody(): string {
      const BR = "\n";
      return `${this.$t("contact.email.bodyPrefix")}${BR}${BR}${this.diagInfo}`;
    },

    /**
     * フォールバック情報を表示
     */
    showFallbackOptions() {
      if (!this.contactData) {
        return;
      }

      // 事前生成されたデータを使用
      this.mailSubject = this.contactData.subject;
      this.mailBody = this.contactData.body;

      // フォールバック表示
      this.showFallback = true;
      this.message = "";
    },

    /**
     * 指定された要素の内容をクリップボードにコピー
     */
    copyToClipboard(type?: string) {
      let el: HTMLElement;
      let successMessage = "";

      switch (type) {
        case "mailTo":
          el = this.$refs.mailToInput as HTMLElement;
          successMessage = this.$t("contact.copyMessages.toSuccess");
          break;
        case "subject":
          el = this.$refs.subjectInput as HTMLElement;
          successMessage = this.$t("contact.copyMessages.subjectSuccess");
          break;
        case "body":
          el = this.$refs.bodyTextarea as HTMLElement;
          successMessage = this.$t("contact.copyMessages.bodySuccess");
          break;
        default:
          // 調査用情報のコピー（既存機能）
          el = this.$refs.textarea as HTMLElement;
          successMessage = this.$t("contact.copyMessages.diagInfoSuccess");
      }

      this.performCopy(el, successMessage);
    },

    /**
     * すべてのメール情報をコピー
     */
    copyAllMailInfo() {
      const allInfo = `${this.$t("contact.fallbackEmailInfo.toLabel")}${this.mailTo}\n${this.$t(
        "contact.fallbackEmailInfo.subjectLabel"
      )}${this.mailSubject}\n\n${this.$t("contact.fallbackEmailInfo.bodyLabel")}\n${this.mailBody}`;

      // 一時的なテキストエリアを作成してコピー
      const tempTextarea = document.createElement("textarea");
      tempTextarea.value = allInfo;
      document.body.appendChild(tempTextarea);

      this.performCopy(tempTextarea, this.$t("contact.copyMessages.allSuccess"));

      document.body.removeChild(tempTextarea);
    },

    /**
     * 実際のコピー処理
     */
    performCopy(element: HTMLElement, successMessage: string) {
      try {
        const el = element as HTMLInputElement | HTMLTextAreaElement;

        if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
          // iOS対応
          const range = document.createRange();
          el.contentEditable = "true";
          el.readOnly = false;
          range.selectNodeContents(el);

          const s = window.getSelection();
          s!.removeAllRanges();
          s!.addRange(range);

          if (el.setSelectionRange) {
            el.setSelectionRange(0, 999999);
          }

          el.contentEditable = "false";
          el.readOnly = true;
        } else {
          el.select();
        }

        if (document.execCommand("copy")) {
          this.message = successMessage;
        } else {
          this.message = this.$t("contact.copyMessages.failure");
        }
      } catch (error) {
        console.error("コピー処理でエラーが発生しました:", error);
        this.message = this.$t("contact.copyMessages.failure");
      }
    },

    /**
     * フォールバック表示を隠す
     */
    hideFallback() {
      this.showFallback = false;
      this.message = "";
    },
  },
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import "@/style-common.scss";
.contact {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 10px;
  padding-bottom: 100px;
  text-align: center;
  @include mq(sp) {
    padding-right: 15px;
    padding-left: 15px;
  }
}

.contact-message {
  font-size: var(--font-size-sm);
  margin-bottom: 20px;
  text-align: left;
  .contact-message-title {
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
  }
}

.root {
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

h1 {
  margin-top: 50px;
  text-align: center;
  color: var(--color-text-normal);
}

h2 {
  margin-top: 30px;
  margin-bottom: 5px;
  text-align: center;
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
}

p {
  font-size: var(--font-size-sm);
  line-height: 160%;
  color: var(--color-text-normal);
}

textarea {
  width: 90%;
  height: 100px;
  border: solid 1px var(--color-border);
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
}
button {
  background-color: var(--color-background-normal);
  border: solid 1px var(--color-border);
  color: var(--color-text-normal);
}
.button-copy {
  text-decoration: none;
  width: 91%;
  margin: 0px;
  padding: 5px 0;
  border-radius: 0px;
  text-align: center;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
}

.button {
  text-decoration: none;
  width: 200px;
  border-radius: var(--radius-sm);
  text-align: center;
}
.bold {
  font-weight: var(--font-weight-bold);
}
/*mailのお問い合わせボタンのレイアウト*/
.email {
  background-color: #34c8e2;
  border: solid 3px #34c8e2;
  color: var(--color-text-on-fixed-bg);
  font-weight: var(--font-weight-bold);
  text-align: center;
  margin-bottom: 15px;
}
.email img {
  display: inline-block;
  width: 20px;
  margin-left: 25px;
  margin-right: 12px;
  float: left;
}
.email-txt {
  display: inline;
  font-size: var(--font-size-sm);
  margin: 0;
  float: left;
}
.info {
  color: var(--color-text-low);
}

/* 期間限定お知らせボックス */
.notice-box {
  width: 100%;
  margin-bottom: 20px;
  padding: 15px 20px;
  border: 2px solid var(--color-warning, #e67e22);
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-muted);
  text-align: left;
  box-sizing: border-box;
  @include mq(sp) {
    margin-right: 2px;
    margin-left: 2px;
    width: calc(100% - 4px);
  }
}

.notice-title {
  margin: 0 0 10px 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: 150%;
  color: var(--color-warning, #e67e22);
}

.notice-message {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-warning, #e67e22);
  line-height: 160%;
}

/* フォールバックリンクのスタイル */
.fallback-link {
  margin: 10px 0 20px 0;
  text-align: center;
}

.link-button {
  background: none;
  border: none;
  color: var(--color-text-normal);
  text-decoration: underline;
  cursor: pointer;
  font-size: var(--font-size-sm);
  padding: 5px 10px;
  border-radius: var(--radius-xs);
  position: relative;
  @include hover-overlay;
}

/* フォールバック表示のスタイル */
.fallback-section {
  margin-top: 20px;
  padding: 20px;
  border: 2px solid var(--color-border);
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-subtle);
  text-align: left;

  h3 {
    color: var(--color-text-normal);
    margin-top: 0;
    margin-bottom: 10px;
    font-size: var(--font-size-md);
  }

  p {
    color: var(--color-text-normal);
    margin-bottom: 15px;
    font-size: var(--font-size-sm);
    line-height: 160%;
  }
}

.fallback-info {
  margin-bottom: 20px;
}

.info-item {
  margin-bottom: 15px;

  strong {
    display: block;
    margin-bottom: 5px;
    color: var(--color-text-normal);
    font-size: var(--font-size-sm);
  }
}

.input-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;

  .copy-input,
  .copy-textarea {
    flex: 1;
    margin-right: 0;
  }

  .copy-btn {
    flex-shrink: 0;
  }
}

.copy-input {
  padding: 8px;
  border: solid 1px var(--color-border);
  border-radius: 0px;
  font-size: var(--font-size-sm);
  margin-bottom: 5px;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
}

.copy-textarea {
  height: 120px;
  padding: 8px;
  border: solid 1px var(--color-border);
  border-radius: 0px;
  font-size: var(--font-size-sm);
  margin-bottom: 5px;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
  resize: vertical;
  font-family: inherit;
}

.copy-btn {
  width: 70px;
  padding: 8px 5px;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
  border: solid 1px var(--color-border);
  border-radius: 0px;
  cursor: pointer;
  font-size: var(--font-size-xs);
  vertical-align: top;
  margin-top: 0;
  position: relative;
  @include hover-overlay;
}

.copy-all-btn {
  background-color: #34c8e2;
  color: var(--color-text-on-fixed-bg);
  border: solid 3px #34c8e2;
  padding: 12px 20px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  text-align: center;

  &:hover {
    background-color: darken(#34c8e2, 10%);
    border-color: darken(#34c8e2, 10%);
  }
}

.close-btn {
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
  border: solid 1px var(--color-border);
  padding: 12px 20px;
  border-radius: 0px;
  cursor: pointer;
  font-size: var(--font-size-sm);
  position: relative;
  @include hover-overlay;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
}
</style>
